import React, { useState, useEffect } from 'react';
import { User, Building2, Phone, MapPin, Lock, Save, Eye, EyeOff, Mail, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProfile() {
    const { user, updateUser } = useAuth();
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('prixdeal_token');
        if (token) {
            axios.get(`${API_URL}/api/admin/permissions`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => setIsSuperAdmin(!!res.data?.is_super_admin))
                .catch(() => {});
        }
    }, []);

    const [profile, setProfile] = useState({
        company_name: user?.company_name || '',
        contact_name: user?.contact_name || '',
        phone: user?.phone || '',
        address: user?.address || '',
        siret: user?.siret || '',
    });

    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [cleanTargets, setCleanTargets] = useState({
        orders: false,
        quotes: false,
        weekly_offers: false,
        custom_offers: false,
        notifications: false,
        contacts: false,
        visitors: false,
        emails: false,
    });

    const CLEAN_LABELS = {
        orders: 'Commandes',
        quotes: 'Demandes de prix',
        weekly_offers: 'Offres de la semaine',
        custom_offers: 'Offres personnalisées',
        notifications: 'Notifications',
        contacts: 'Messages contact',
        visitors: 'Historique visiteurs',
        emails: 'Historique emails',
    };

    const toggleTarget = (key) => setCleanTargets(prev => ({ ...prev, [key]: !prev[key] }));
    const selectAll = () => {
        const allSelected = Object.values(cleanTargets).every(v => v);
        const next = {};
        Object.keys(cleanTargets).forEach(k => { next[k] = !allSelected; });
        setCleanTargets(next);
    };

    const handleCleanup = async () => {
        const targets = Object.entries(cleanTargets).filter(([, v]) => v).map(([k]) => k);
        if (targets.length === 0) { toast.error('Sélectionnez au moins une catégorie'); return; }
        if (!window.confirm(`Supprimer définitivement les données sélectionnées ?\n\n${targets.map(t => '• ' + CLEAN_LABELS[t]).join('\n')}\n\nCette action est irréversible.`)) return;
        setCleaning(true);
        try {
            const token = localStorage.getItem('prixdeal_token');
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cleanup`, { targets }, { headers: { Authorization: `Bearer ${token}` } });
            const d = res.data.deleted || {};
            const summary = Object.entries(d).map(([k, v]) => `${CLEAN_LABELS[k] || k}: ${v}`).join(', ');
            toast.success(`Nettoyage terminé — ${summary}`);
            setCleanTargets(prev => { const r = {}; Object.keys(prev).forEach(k => { r[k] = false; }); return r; });
        } catch (e) {
            const msg = e.response?.status === 403
                ? 'Accès refusé — seul le super admin peut effectuer le nettoyage'
                : (e.response?.data?.detail || 'Erreur lors du nettoyage');
            toast.error(msg);
        } finally {
            setCleaning(false);
        }
    };
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await authAPI.updateProfile(profile);
            updateUser(res.data);
            toast.success('Profil mis a jour avec succes');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwords.new_password !== passwords.confirm_password) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        if (passwords.new_password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caracteres');
            return;
        }
        setChangingPassword(true);
        try {
            await authAPI.changePassword({
                current_password: passwords.current_password.normalize('NFC').trim(),
                new_password: passwords.new_password.normalize('NFC').trim(),
            });
            toast.success('Mot de passe modifie avec succes');
            setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-profile-page">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle" data-testid="admin-profile-title">
                    Mon Profil
                </h1>
                <p className="text-muted-foreground mt-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {user?.email}
                </p>
            </div>

            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-jungle">
                        <User className="w-5 h-5" />
                        Informations personnelles
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleProfileSave} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="company_name" className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                    Nom de l'entreprise
                                </Label>
                                <Input
                                    id="company_name"
                                    data-testid="admin-profile-company"
                                    value={profile.company_name}
                                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_name" className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    Nom du responsable
                                </Label>
                                <Input
                                    id="contact_name"
                                    data-testid="admin-profile-contact"
                                    value={profile.contact_name}
                                    onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="phone" className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    Telephone
                                </Label>
                                <Input
                                    id="phone"
                                    data-testid="admin-profile-phone"
                                    value={profile.phone}
                                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="siret" className="flex items-center gap-2">
                                    SIRET
                                </Label>
                                <Input
                                    id="siret"
                                    data-testid="admin-profile-siret"
                                    value={profile.siret}
                                    onChange={(e) => setProfile({ ...profile, siret: e.target.value })}
                                    placeholder="Optionnel"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="address" className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                Adresse
                            </Label>
                            <Input
                                id="address"
                                data-testid="admin-profile-address"
                                value={profile.address}
                                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                            />
                        </div>
                        <div className="flex justify-end pt-2">
                            <Button type="submit" disabled={saving} data-testid="admin-profile-save" className="bg-leaf hover:bg-leaf/90 text-white">
                                <Save className="w-4 h-4 mr-2" />
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-jungle">
                        <Lock className="w-5 h-5" />
                        Changer le mot de passe
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">Mot de passe actuel</Label>
                            <div className="relative">
                                <Input
                                    id="current_password"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    data-testid="admin-current-password"
                                    value={passwords.current_password}
                                    onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                                    required
                                />
                                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                                    {showCurrentPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="new_password">Nouveau mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="new_password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        data-testid="admin-new-password"
                                        value={passwords.new_password}
                                        onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                                        required
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowNewPassword(!showNewPassword)}>
                                        {showNewPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm_password">Confirmer</Label>
                                <div className="relative">
                                    <Input
                                        id="confirm_password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        data-testid="admin-confirm-password"
                                        value={passwords.confirm_password}
                                        onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                                        required
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <Button type="submit" disabled={changingPassword} data-testid="admin-change-password-btn" variant="outline" className="border-jungle text-jungle hover:bg-jungle/10">
                                <Lock className="w-4 h-4 mr-2" />
                                {changingPassword ? 'Modification...' : 'Changer le mot de passe'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* CLEANUP / NETTOYAGE — Super Admin only */}
            {isSuperAdmin && (
            <Card className="bg-white/80 backdrop-blur-xl border-red-200/50 shadow-sm rounded-2xl">
                <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                        <Trash2 className="w-5 h-5" />
                        Nettoyage des données de test
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Cette opération supprime définitivement les données sélectionnées. Les produits et les clients ne sont pas affectés.</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(CLEAN_LABELS).map(([key, label]) => (
                            <button
                                key={key}
                                onClick={() => toggleTarget(key)}
                                className={`p-3 rounded-xl border text-sm font-medium text-left transition-all ${
                                    cleanTargets[key]
                                        ? 'border-red-400 bg-red-50 text-red-700'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                }`}
                                data-testid={`clean-${key}`}
                            >
                                <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                        cleanTargets[key] ? 'border-red-500 bg-red-500' : 'border-gray-300'
                                    }`}>
                                        {cleanTargets[key] && <span className="text-white text-xs">✓</span>}
                                    </div>
                                    {label}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={selectAll}
                            className="rounded-xl text-sm"
                            data-testid="clean-select-all"
                        >
                            {Object.values(cleanTargets).every(v => v) ? 'Tout désélectionner' : 'Tout sélectionner'}
                        </Button>
                        <Button
                            onClick={handleCleanup}
                            disabled={cleaning || !Object.values(cleanTargets).some(v => v)}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm"
                            data-testid="clean-submit"
                        >
                            {cleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                            {cleaning ? 'Nettoyage...' : 'Nettoyer les données'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
            )}
        </div>
    );
}
